/**
 * MessageAtom.tsx
 *
 * A generic message/alert atom wrapping PrimeReact's Message component
 * with design system styling and consistent API.
 */

import React, { forwardRef } from 'react';
import { Message, MessageProps } from 'primereact/message';
import { useTranslation } from 'localization';
import './MessageAtom.scss';

// 'neutral' is additive (EAINT-921): PrimeReact's own Message component has no matching severity,
// so it's passed through as 'info' to PrimeReact and the visual distinction comes entirely from
// the `message-atom--severity-neutral` CSS class — used for informational banners (e.g. an
// altitude context banner) that should read as calm chrome, not as an info callout.
export type MessageSeverity = 'success' | 'info' | 'warn' | 'error' | 'neutral';
export type MessageVariant = 'default' | 'outlined' | 'filled';

// 'altitude' is additive (EAINT-928 Phase 1 P6): the sphere type editor's document reads the
// same at four altitudes (parent, this-type, approach, sphere), and a banner above it states
// where the reader stands and what may be set from there. `this-type` is the only altitude that
// authors the type — the caller renders no banner for it at all (`alt.banner === null` in the
// source prototype), so it carries no tone override here; it exists as a value only so a
// consuming component's own altitude enum maps onto this prop without a lossy translation.
// Independent of `severity`: an altitude banner is always the calm "neutral" tone (provenance,
// not an alert), so `altitude` overrides the icon and adds its own tint class rather than
// standing in for severity.
export type MessageAltitude = 'parent' | 'this-type' | 'approach' | 'sphere';

export interface MessageAtomProps extends Omit<MessageProps, 'className' | 'severity'> {
  /** Severity level determines the color and icon */
  severity?: MessageSeverity;
  /**
   * Where the reader stands relative to the document this message is about (the sphere type
   * editor's altitude banner). Additive: selects an icon and a tone tint layered over
   * `severity`; pass `severity="neutral"` alongside it (the banner's own tone, per the prototype)
   * unless a caller has a specific reason to alert/warn at a given altitude instead.
   */
  altitude?: MessageAltitude;
  /** Visual variant of the message */
  variant?: MessageVariant;
  /** Additional CSS class names */
  className?: string;
  /** Whether the message can be dismissed */
  dismissible?: boolean;
  /** Callback when message is dismissed */
  onDismiss?: () => void;
  /**
   * Overrides the dismiss control's accessible label. Defaults to the
   * translated `common.dismiss` — the atom translates its own chrome rather
   * than pushing that onto callers, because an optional label prop with an
   * English default ships `aria-label="Dismiss"` to French screen readers the
   * first time someone forgets it. Pass this only when "Dismiss" is too vague
   * for the context (several dismissible notices on one screen).
   */
  dismissLabel?: string;
  /**
   * How the dismiss control reads (EAINT-933 Phase 4 Reuse Audit — `t-crit` shows the word
   * "Dismiss", not an ✕): `'icon'` (default) keeps the existing corner ✕; `'text'` renders a
   * visible text control inline after the message content, matching the prototype's ambient
   * critique card (`.t-crit .a-btn-quiet`).
   */
  dismissVariant?: 'icon' | 'text';
  /** Custom icon class (overrides default severity icon) */
  iconClass?: string;
  /** `data-datum` on the message's own wrapper — a caller mapping this instance to a contract row. */
  datum?: string;
  /** `data-datum` on the dismiss control, when `dismissible` — see `datum`. */
  dismissDatum?: string;
  /**
   * The message sits inside a live region of its own (an `<output>` or `role="status"` container),
   * which announces it. PrimeReact stamps `role="alert"` and `aria-live="polite"` on every Message;
   * nested in a region they announce it twice, so here the message renders as a plain container.
   * It replaces any `pt.root` the caller passes.
   */
  inLiveRegion?: boolean;
}

const MessageAtom = forwardRef<Message, MessageAtomProps>((props, ref) => {
  const {
    severity = 'info',
    altitude,
    variant = 'default',
    className = '',
    dismissible = false,
    onDismiss,
    dismissLabel,
    dismissVariant = 'icon',
    iconClass,
    datum,
    dismissDatum,
    inLiveRegion = false,
    pt,
    ...rest
  } = props;

  const { t } = useTranslation();

  // Build CSS classes
  const classes = [
    'message-atom',
    `message-atom--severity-${severity}`,
    `message-atom--variant-${variant}`,
    altitude ? `message-atom--altitude-${altitude}` : '',
    dismissible ? 'message-atom--dismissible' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  // Map severity to icon if not custom
  const severityIcons: Record<MessageSeverity, string> = {
    success: 'pi pi-check-circle',
    info: 'pi pi-info-circle',
    warn: 'pi pi-exclamation-triangle',
    error: 'pi pi-times-circle',
    neutral: 'pi pi-info-circle',
  };

  // Altitude icons, matching the prototype's harness (`trail-app.jsx:108`): parent → git-merge
  // (an ancestor's decision merging in), approach → package (a deployment), sphere → circle-dot
  // (one instance). `this-type` has no banner in the source and so no icon of its own — it falls
  // through to the severity icon above if ever rendered.
  const altitudeIcons: Record<Exclude<MessageAltitude, 'this-type'>, string> = {
    parent: 'pi pi-sitemap',
    approach: 'pi pi-box',
    sphere: 'pi pi-circle-fill',
  };

  // PrimeReact's MessageProps#severity has no 'neutral' option; 'info' keeps its internal markup
  // (icon slot) sane while our own CSS class carries the neutral treatment. PrimeReact stamps
  // `role="alert"` on every Message; a neutral message is provenance ("why this document is the
  // way it is"), not something that happened, so it is a `status` region — announced politely,
  // never assertively (ux-craft A3). An explicit `role` from the caller still wins.
  const primeReactSeverity = severity === 'neutral' ? 'info' : severity;
  const neutralRole = severity === 'neutral' ? { role: 'status' } : {};
  // PassThrough props override the root's own attributes, so the live-region pair is cleared there
  // (`inLiveRegion` owns the root's pass-through; other sections of `pt` pass through unchanged).
  const rootPt = inLiveRegion ? { ...pt, root: { role: undefined, 'aria-live': undefined } } : pt;

  return (
    <div className={classes} data-datum={datum} data-kind={datum ? 'callout' : undefined}>
      <Message
        ref={ref}
        severity={primeReactSeverity}
        icon={iconClass || (altitude && altitude !== 'this-type' ? altitudeIcons[altitude] : severityIcons[severity])}
        {...(inLiveRegion ? {} : neutralRole)}
        {...rest}
        pt={rootPt}
      />
      {dismissible &&
        (dismissVariant === 'text' ? (
          <button
            type="button"
            className="message-atom__dismiss message-atom__dismiss--text"
            onClick={onDismiss}
            data-datum={dismissDatum}
            data-kind={dismissDatum ? 'button' : undefined}
            data-variant={dismissDatum ? 'quiet' : undefined}
          >
            {dismissLabel ?? t('common.dismiss')}
          </button>
        ) : (
          <button
            type="button"
            className="message-atom__dismiss"
            onClick={onDismiss}
            aria-label={dismissLabel ?? t('common.dismiss')}
            data-datum={dismissDatum}
          >
            <i className="pi pi-times" />
          </button>
        ))}
    </div>
  );
});

MessageAtom.displayName = 'MessageAtom';

export default MessageAtom;
